import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from "@/components/ui/command";
import { DialogClose } from "@/components/ui/dialog";
import { cn } from "@/utils/cn";

import { useGlobalSearchHotkey } from "./use-global-search-hotkey";
import { matchesFilter, useGlobalSearchIndex } from "./use-global-search-index";
import { useRecents } from "./use-recents";
import { useSearchNavigation } from "./use-search-navigation";
import type { ISearchEntry, SearchFilterId } from "./types";

import { SearchEmptyState } from "./components/SearchEmptyState";
import { SearchFilterChips } from "./components/SearchFilterChips";
import { SearchFooter } from "./components/SearchFooter";
import { SearchLoadingSkeleton } from "./components/SearchLoadingSkeleton";
import { SearchResultRow } from "./components/SearchResultRow";
import { SearchSectionHeading } from "./components/SearchSectionHeading";
import { SearchTrigger } from "./components/SearchTrigger";

export const GlobalSearch = () => {
  const { t } = useTranslation("common");
  const { entries, groups, filters, isLoading } = useGlobalSearchIndex();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<SearchFilterId>("all");

  const recents = useRecents();
  const { navigateTo, isNavigating, reset: resetNavigating } = useSearchNavigation(setOpen);

  const openSearch = useCallback(() => setOpen(true), []);
  useGlobalSearchHotkey(openSearch);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setActiveFilter("all");
      resetNavigating();
    }
  }, [open, resetNavigating]);

  const handleFilterChange = useCallback((id: SearchFilterId, viaPointer: boolean) => {
    setActiveFilter(id);
    // Clicking a chip with the mouse leaves focus on the button; hand it back to
    // the input so typing (and cmdk's ↑↓ list nav) keeps working. Keyboard chip
    // navigation (viaPointer=false) intentionally keeps focus on the chip row.
    if (viaPointer) {
      queueMicrotask(() =>
        (document.querySelector("[cmdk-input]") as HTMLElement | null)?.focus(),
      );
    }
  }, []);

  const filteredGroups = useMemo(
    () =>
      groups
        .map((group) => ({
          ...group,
          entries: group.entries.filter((e) => matchesFilter(e, activeFilter)),
        }))
        .filter((group) => group.entries.length > 0),
    [groups, activeFilter],
  );

  const entryByPath = useMemo(() => {
    const map = new Map<string, ISearchEntry>();
    for (const e of entries) map.set(e.path, e);
    return map;
  }, [entries]);

  const recentEntries = useMemo(
    () =>
      recents.paths
        .map((p) => entryByPath.get(p))
        .filter((e): e is ISearchEntry => Boolean(e)),
    [recents.paths, entryByPath],
  );

  const handleSelect = useCallback(
    (entry: ISearchEntry) => {
      recents.add(entry.path);
      navigateTo(entry.path);
    },
    [navigateTo, recents],
  );

  const handleRemoveRecent = useCallback(
    (entry: ISearchEntry) => recents.remove(entry.path),
    [recents],
  );

  const showRecents = activeFilter === "all" && !query && recentEntries.length > 0;

  return (
    <>
      <SearchTrigger onOpen={openSearch} />

      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title={t("search.title")}
        description={t("search.description")}
        className={cn(
          "rounded-xs border-border bg-surface shadow-xl sm:max-w-140",
          "data-[state=open]:zoom-in-90 data-[state=open]:slide-in-from-top-4",
          "data-[state=open]:duration-320 data-[state=open]:ease-[cubic-bezier(0.34,1.56,0.64,1)]",
          isNavigating
            ? "data-[state=closed]:duration-0"
            : "data-[state=closed]:zoom-out-95 data-[state=closed]:slide-out-to-top-2 data-[state=closed]:duration-150 data-[state=closed]:ease-in",
        )}
      >
        <CommandInput
          value={query}
          onValueChange={setQuery}
          placeholder={t("header.search_page_placeholder")}
          iconClassName="text-primary opacity-100"
          rightElement={
            <DialogClose className="flex size-6 shrink-0 items-center justify-center rounded-xs border border-border bg-background text-muted-foreground shadow-sm transition-colors hover:border-primary hover:text-primary">
              <X className="size-3.5" />
              <span className="sr-only">{t("header.close_search")}</span>
            </DialogClose>
          }
        />

        {!isLoading && (
          <SearchFilterChips filters={filters} active={activeFilter} onChange={handleFilterChange} />
        )}

        <CommandList className="max-h-105">
          {isLoading ? (
            <SearchLoadingSkeleton />
          ) : (
            <>
              <CommandEmpty>
                <SearchEmptyState query={query} />
              </CommandEmpty>

              {showRecents && (
                <>
                  <SearchSectionHeading
                    label={t("search.recent")}
                    action={
                      <button
                        type="button"
                        onClick={recents.clear}
                        className="text-[11px] text-muted-foreground transition-colors hover:text-foreground"
                      >{t("company:time_attendance.clear_all")}</button>
                    }
                  />
                  <CommandGroup>
                    {recentEntries.map((entry) => (
                      <SearchResultRow
                        key={`recent-${entry.path}`}
                        entry={entry}
                        valuePrefix="recent "
                        isRecent
                        onSelect={handleSelect}
                        onRemove={handleRemoveRecent}
                      />
                    ))}
                  </CommandGroup>
                </>
              )}

              {filteredGroups.map((group) => (
                <CommandGroup
                  key={group.space}
                  heading={group.label}
                  className="**:[[cmdk-group-heading]]:px-3 **:[[cmdk-group-heading]]:text-[11px] **:[[cmdk-group-heading]]:font-semibold **:[[cmdk-group-heading]]:uppercase **:[[cmdk-group-heading]]:tracking-wider"
                >
                  {group.entries.map((entry) => (
                    <SearchResultRow key={entry.path} entry={entry} onSelect={handleSelect} />
                  ))}
                </CommandGroup>
              ))}
            </>
          )}
        </CommandList>

        <SearchFooter />
      </CommandDialog>
    </>
  );
};
